
import React, { useMemo } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import MapPinIcon from './icons/MapPinIcon';
import SettingsIcon from './icons/SettingsIcon';
import DocumentIcon from './icons/DocumentIcon';
import HeartIcon from './icons/HeartIcon';
import FacebookIcon from './icons/FacebookIcon';
import InstagramIcon from './icons/InstagramIcon';
import { openAttachment } from '../utils/fileUtils';
import type { EmbeddedMedia } from '../types';

/**
 * Official project page for bands and ensembles.
 */
const BandPage: React.FC = () => {
    const { currentUser } = useAuth();
    const { 
        viewingBand, 
        handleViewProfile, 
        handleClaimBand, 
        handleNavigation, 
        handleViewFanList, 
        handleToggleBandFan,
        navigationOrigin 
    } = useNavigation();

    const locationDisplay = useMemo(() => {
        if (!viewingBand) return 'Global';
        const parts = [];
        if (viewingBand.city) parts.push(viewingBand.city);
        if (viewingBand.region) parts.push(viewingBand.region);
        if (viewingBand.country) parts.push(viewingBand.country);
        return parts.length > 0 ? parts.join(', ') : 'Global';
    }, [viewingBand]);

    if (!viewingBand) return null;

    const isMember = currentUser && viewingBand.members.some(m => m.handle === currentUser.handle);
    const fansCount = viewingBand.fans?.length || 0;
    const isFanOfThisBand = currentUser && viewingBand.fans?.includes(currentUser.handle);

    const hasMedia = viewingBand.embeddedMedia && viewingBand.embeddedMedia.length > 0;
    const hasPhotos = viewingBand.photos && viewingBand.photos.length > 0;
    const hasIdentity = viewingBand.instruments?.length > 0 || viewingBand.genres?.length > 0;
    const hasSocials = viewingBand.socialLinks?.facebook || viewingBand.socialLinks?.instagram;

    return (
        <div className="space-y-6 font-urbanist pb-12">
            <div className="flex justify-between items-center">
                <button onClick={() => handleNavigation(navigationOrigin || 'feed')} className="flex items-center space-x-2 text-brand-muted hover:text-white font-semibold transition-colors">
                    <ArrowLeftIcon className="h-5 w-5" />
                    <span>Back</span>
                </button>
                {isMember && (
                    <button 
                        onClick={() => handleNavigation('manageBand', { band: viewingBand })}
                        className="flex items-center space-x-2 bg-gray-700 hover:bg-gray-600 text-white font-bold px-4 py-2 rounded-full text-xs tracking-widest transition-all shadow-lg"
                    >
                        <SettingsIcon className="h-4 w-4" />
                        <span>Manage Project</span>
                    </button>
                )}
            </div>

            <div className="bg-brand-surface rounded-lg overflow-hidden border border-gray-800 shadow-sm relative">
                <div className="h-48 md:h-64 bg-gray-800 bg-cover bg-center" style={{ backgroundImage: viewingBand.bannerUrl ? `url(${viewingBand.bannerUrl})` : 'none' }}>
                    {!viewingBand.bannerUrl && (
                        <div className="w-full h-full flex items-center justify-center text-gray-700">
                             <MapPinIcon className="h-20 w-20 opacity-10" />
                        </div>
                    )}
                </div>
                <div className="px-6 pb-6 relative">
                    <div className="flex flex-col md:flex-row md:items-end -mt-16 md:-mt-20 space-y-4 md:space-y-0 md:space-x-6">
                        <img src={viewingBand.avatarUrl} alt={viewingBand.name} className="h-32 w-32 md:h-40 md:w-40 rounded-2xl border-4 border-brand-surface shadow-xl bg-gray-700 object-cover" />
                        <div className="flex-1">
                            <h1 className="text-3xl md:text-5xl font-black text-white tracking-tighter leading-none uppercase">{viewingBand.name}</h1>
                            <div className="flex items-center text-brand-muted mt-2 space-x-4">
                                <div className="flex items-center"><MapPinIcon className="h-4 w-4 mr-1 text-brand-orange" /><span>{locationDisplay}</span></div>
                                <span className="bg-gray-800 text-gray-300 text-[10px] font-bold px-2 py-0.5 rounded tracking-widest uppercase">{viewingBand.isClaimed ? 'Verified Project' : 'Community Profile'}</span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {!isMember && (
                                <button 
                                    onClick={() => handleToggleBandFan(viewingBand.id)}
                                    className={`flex items-center space-x-2 px-6 py-3 rounded-full font-bold tracking-widest text-xs transition-all shadow-lg ${
                                        isFanOfThisBand 
                                        ? 'bg-pink-600 text-white shadow-pink-600/20' 
                                        : 'bg-gray-800 text-brand-muted border border-gray-700 hover:border-pink-500 hover:text-white'
                                    }`}
                                >
                                    <HeartIcon className={`h-4 w-4 ${isFanOfThisBand ? 'fill-current' : ''}`} />
                                    <span>{isFanOfThisBand ? "I'm a Fan" : "Become a Fan"}</span>
                                </button>
                            )}
                            {!isMember && !viewingBand.isClaimed && (
                                <button onClick={() => handleClaimBand(viewingBand.id)} className="bg-brand-orange text-white font-bold px-8 py-3 rounded-full hover:bg-orange-600 transition-all tracking-widest text-xs shadow-lg shadow-brand-orange/20">Claim this Page</button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                        <h2 className="text-xl font-black text-white mb-4 tracking-tight border-l-4 border-brand-orange pl-4 uppercase">About the Project</h2>
                        <p className="text-gray-300 whitespace-pre-wrap leading-relaxed font-medium">{viewingBand.bio || 'No biography provided yet.'}</p>
                    </div>

                    {hasMedia && (
                        <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                            <h2 className="text-xl font-black text-white mb-6 tracking-tight border-l-4 border-brand-orange pl-4 uppercase">Media Showcase</h2>
                            <div className="space-y-6">
                                {viewingBand.embeddedMedia?.map((item: EmbeddedMedia) => (
                                    <div key={item.id} className="rounded-xl overflow-hidden bg-black/40 border border-gray-800 shadow-inner">
                                        {item.type === 'youtube' && (
                                            <iframe width="100%" height="315" src={item.url} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="YouTube video player"></iframe>
                                        )}
                                        {item.type === 'spotify' && (
                                            <iframe src={item.url} width="100%" height="152" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" title="Spotify player"></iframe>
                                        )}
                                        {item.type === 'soundcloud' && (
                                            <iframe width="100%" height="166" scrolling="no" frameBorder="no" allow="autoplay" src={item.url} title="SoundCloud player"></iframe>
                                        )}
                                        {item.type === 'unknown' && (
                                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="p-4 block text-brand-orange hover:underline font-bold uppercase tracking-widest text-xs">External Media Link &rarr;</a>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {hasPhotos && (
                        <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                            <h2 className="text-xl font-black text-white mb-6 tracking-tight border-l-4 border-brand-orange pl-4 uppercase">Gallery</h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {viewingBand.photos?.map(photo => (
                                    <div key={photo.id} className="aspect-square rounded-xl overflow-hidden border border-gray-800 shadow-md transition-transform hover:scale-[1.02]">
                                        <img src={photo.url} className="w-full h-full object-cover" alt="Band showcase" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm overflow-hidden">
                        <div 
                            className="flex justify-between items-center mb-6 cursor-pointer group"
                            onClick={() => handleViewFanList(viewingBand)}
                        >
                            <h2 className="text-xl font-black text-white tracking-tight border-l-4 border-pink-500 pl-4 group-hover:text-pink-500 transition-colors flex items-center gap-2 uppercase">
                                Fans ({fansCount})
                                <span className="text-[10px] bg-gray-800 px-2 py-1 rounded text-brand-muted opacity-0 group-hover:opacity-100 transition-opacity tracking-widest">View All</span>
                            </h2>
                            {isMember && <span className="bg-pink-500/10 text-pink-400 text-[10px] font-bold px-2 py-1 rounded-full tracking-widest uppercase">Marketing Insight</span>}
                        </div>
                        <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                            {(viewingBand.fans || []).slice(0, 8).map(fanHandle => (
                                <div key={fanHandle} className="aspect-square bg-gray-800 rounded-lg overflow-hidden border border-gray-700 hover:border-pink-500/50 transition-colors cursor-pointer">
                                    <img src={`https://i.pravatar.cc/150?u=${fanHandle}`} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all" alt="" />
                                </div>
                            ))}
                            {fansCount === 0 && <p className="col-span-full text-brand-muted text-sm italic py-4">No fans yet. Be the first!</p>}
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                        <h2 className="text-xl font-black text-white mb-4 tracking-tight border-l-4 border-brand-orange pl-4 uppercase">Lineup</h2>
                        <ul className="space-y-3">
                            {viewingBand.members.map(member => (
                                <li key={member.handle} onClick={() => handleViewProfile(member as any)} className="flex items-center space-x-3 bg-gray-800/40 p-2 rounded-xl border border-gray-800 hover:border-brand-orange/50 transition-all cursor-pointer group">
                                    <img src={member.avatarUrl} alt={member.name} className="h-10 w-10 rounded-lg border-2 border-gray-700 group-hover:border-brand-orange transition-colors" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-white truncate tracking-tight uppercase">{member.name}</p>
                                        <p className="text-[10px] text-brand-muted truncate font-mono">@{member.handle}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {hasIdentity && (
                        <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                            <h2 className="text-xl font-black text-white mb-4 tracking-tight border-l-4 border-brand-orange pl-4 uppercase">Musical Profile</h2>
                            <div className="space-y-4">
                                {viewingBand.instruments?.length > 0 && (
                                    <div>
                                        <label className="block text-[10px] font-black text-brand-muted mb-2 uppercase tracking-[0.2em]">Core Instruments</label>
                                        <div className="flex flex-wrap gap-2">
                                            {viewingBand.instruments.map(i => <span key={i} className="bg-brand-orange/10 text-brand-orange text-[10px] font-black px-2 py-0.5 rounded border border-brand-orange/20 uppercase tracking-widest">#{i}</span>)}
                                        </div>
                                    </div>
                                )}
                                {viewingBand.genres?.length > 0 && (
                                    <div>
                                        <label className="block text-[10px] font-black text-brand-muted mb-2 uppercase tracking-[0.2em]">Main Genres</label>
                                        <div className="flex flex-wrap gap-2">
                                            {viewingBand.genres.map(g => <span key={g} className="bg-blue-500/10 text-blue-400 text-[10px] font-black px-2 py-0.5 rounded border border-blue-500/20 uppercase tracking-widest">#{g}</span>)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {(viewingBand.rider || (viewingBand.riderFiles && viewingBand.riderFiles.length > 0)) && (
                        <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                            <h2 className="text-xl font-black text-white mb-4 tracking-tight border-l-4 border-brand-orange pl-4 uppercase">Tech Specs</h2>
                            {viewingBand.rider && <p className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed mb-4">{viewingBand.rider}</p>}
                            {viewingBand.riderFiles && viewingBand.riderFiles.length > 0 && (
                                <ul className="space-y-2">
                                    {viewingBand.riderFiles.map(file => (
                                        <li key={file.id}>
                                            <button 
                                                onClick={() => openAttachment(file)}
                                                className="flex items-center space-x-2 text-sm text-brand-orange hover:underline text-left w-full focus:outline-none"
                                            >
                                                <DocumentIcon className="h-4 w-4" />
                                                <span className="truncate font-bold">{file.name}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {hasSocials && (
                        <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                            <h2 className="text-xl font-black text-white mb-4 tracking-tight border-l-4 border-brand-orange pl-4 uppercase">Social Presence</h2>
                            <div className="space-y-3">
                                {viewingBand.socialLinks?.facebook && (
                                    <a href={viewingBand.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-[#1877F2]/10 p-3 rounded-xl border border-[#1877F2]/20 hover:bg-[#1877F2]/20 transition-colors">
                                        <FacebookIcon className="h-5 w-5 text-[#1877F2]" />
                                        <span className="text-xs font-black uppercase text-white tracking-widest">Facebook</span>
                                    </a>
                                )}
                                {viewingBand.socialLinks?.instagram && (
                                    <a href={viewingBand.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-[#E4405F]/10 p-3 rounded-xl border border-[#E4405F]/20 hover:bg-[#E4405F]/20 transition-colors">
                                        <InstagramIcon className="h-5 w-5 text-[#E4405F]" />
                                        <span className="text-xs font-black uppercase text-white tracking-widest">Instagram</span>
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BandPage;
